// Client side of the temp-password onboarding (task #127). Talks to
// hub/netlify/functions/tempPassword.mjs. The temp password itself never
// lives in this bundle - the server returns it so the Director can copy the
// sign-in text; the forced-change gate never needs to know it.

import { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useAuth } from './auth-context';
import type { ManagerTitle, UserRole } from './users';

export interface TempAccountResult {
  uid: string;
  email: string | null;
  tempPassword: string;
  linkedStudents?: number;
  /**
   * Set only on a Director's create, when a bag group already carrying one of
   * the linked dancers was too full to take the new account (bag group plan
   * BG-3). Words for the screen; never part of the sign-in text.
   */
  bagGroupNotice?: string;
}

export interface ChangeEmailResult {
  uid: string;
  email: string;
  oldEmail: string | null;
}

const FRIENDLY: Record<string, string> = {
  sign_in_required: 'Sign in again, then retry.',
  bad_token: 'Your sign-in expired. Refresh the page and try again.',
  director_only: 'Only the Director can add or reset accounts.',
  not_configured_firebase_admin:
    'The studio server is missing its Firebase connection in Netlify, so accounts cannot be created yet.',
  bad_email: 'That email does not look right. Check it and try again.',
  name_required: 'Give them a name so everyone knows who this is.',
  bad_role: 'Pick a role first.',
  invalid_manager_title: 'Pick Manager/Admin or Manager/Executive Teacher.',
  manager_title_requires_manager_role: 'That flavor only applies to the Manager role.',
  email_exists:
    'An account with that email already exists. Use "Reset password" on their row instead.',
  user_not_found: 'That account no longer exists.',
  auth_update_failed: 'The change did not go through. Try again in a moment.',
  auth_create_failed: 'The account could not be created. Try again in a moment.',
  bad_request: 'Something about that request was not right. Try again.',
  not_allowed: 'Only a signed-in family account can add a co-guardian.',
  no_shared_dancers:
    'Add a dancer to your family first, then you can invite their other guardian.',
  managed_dancer_account:
    'This dancer login is managed by their family in Account settings.',
  dancer_identity_check_failed:
    'The server could not safely verify this account. Nothing changed. Try again in a moment.',
};

// Same shape check the server runs (netlify/functions/tempPassword.mjs
// normalizeEmail/isValidEmail) so a bad address gets caught before a round
// trip, without the two ever being able to disagree on what "valid" means.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Same plain-shape check the server uses - good enough to catch typos before a round trip. */
export function isValidEmailAddress(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}

async function call<T>(
  body: Record<string, unknown>,
  friendlyOverrides?: Record<string, string>
): Promise<T> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const idToken = await me.getIdToken();
  const res = await fetch('/.netlify/functions/tempPassword', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok || data.error) {
    const code = data.error || `server_${res.status}`;
    const message = friendlyOverrides?.[code] ?? FRIENDLY[code];
    throw new Error(message || `The account server hit an error (${code}).`);
  }
  return data;
}

/**
 * Director: create a ready-to-use account with the studio temp password.
 * `friendlyOverrides` lets a caller swap in a context-specific message for a
 * given server error code (e.g. "Add a family" wants a different
 * email_exists message than the plain "Add someone" flow) without touching
 * the shared default text.
 */
export async function createWithTempPassword(
  email: string,
  name: string,
  role: UserRole,
  studentIds: string[] = [],
  friendlyOverrides?: Record<string, string>,
  managerTitle?: ManagerTitle | null
): Promise<TempAccountResult> {
  return call<TempAccountResult>(
    { mode: 'create', email, name, role, studentIds, managerTitle },
    friendlyOverrides
  );
}

/** Director: set an existing account's password back to the studio temp password. */
export async function resetToTempPassword(uid: string): Promise<TempAccountResult> {
  return call<TempAccountResult>({ mode: 'reset', uid });
}

/**
 * Family self-serve: add ANOTHER guardian to your OWN family (Bobbie: "quickly
 * as a mom add my husband in as a user"). Creates their login on the studio
 * temp password so you can hand it straight over, and links them to the
 * dancers you pass. The server re-proves each dancer is really yours, forces
 * the role to family, and refuses any email that already has an account - it
 * never touches an existing login.
 */
export async function addCoGuardian(
  email: string,
  name: string,
  studentIds: string[]
): Promise<TempAccountResult> {
  return call<TempAccountResult>(
    { mode: 'add-coguardian', email, name, studentIds },
    {
      email_exists:
        'That email already has a Hub login, so they are all set - just have them sign in. (Forgot the password? Ask the studio to reset it.)',
    }
  );
}

/**
 * Director: change an existing account's sign-in email in place. Only the
 * address they type to sign in moves - role, linked dancers, notification
 * prefs, and acknowledgements all stay exactly as they were.
 */
export async function changeAccountEmail(
  uid: string,
  newEmail: string
): Promise<ChangeEmailResult> {
  return call<ChangeEmailResult>(
    { mode: 'change-email', uid, newEmail },
    {
      email_exists:
        'Another account already signs in with that email. Reset that account instead, or pick a different address.',
    }
  );
}

/** Called by the forced-change gate after updatePassword succeeds. */
export async function markPasswordChanged(): Promise<void> {
  await call<{ ok: boolean }>({ mode: 'password-changed' });
  // The flag lives in custom claims; a forced token refresh makes the
  // clear visible immediately instead of at the next hourly refresh.
  await getAuth().currentUser?.getIdToken(true);
}

/**
 * True while this account is still on the studio starter password. Read
 * from CUSTOM CLAIMS (never the users doc - the live Firestore rules
 * validate the doc against a closed key list, and a planted extra field
 * bricked every self-service update those accounts made). Also honors a
 * legacy doc-planted flag passed in by the caller so accounts damaged
 * before the claims switch still see the gate until their next reset
 * scrubs the field.
 */
export function useMustChangePassword(legacyDocFlag?: boolean): boolean {
  const { user } = useAuth();
  const [claimFlag, setClaimFlag] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setClaimFlag(false);
      return;
    }
    user
      .getIdTokenResult()
      .then((r) => {
        if (!cancelled) setClaimFlag(r.claims.mustChangePassword === true);
      })
      .catch(() => {
        if (!cancelled) setClaimFlag(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);
  return claimFlag || legacyDocFlag === true;
}

/** The copy-paste text the Director sends along with a fresh or reset login. */
export function signInTextFor(email: string, tempPassword: string): string {
  return [
    `You're set up on the LDG Hub!`,
    ``,
    `1. Go to https://ldg-hub-staging.netlify.app`,
    `2. Sign in with email: ${email}`,
    `3. Password: ${tempPassword}`,
    ``,
    `It will ask you to pick your own password the first time. That's it!`,
  ].join('\n');
}
